import React, { useState, useEffect } from 'react';
import { Field, reduxForm } from 'redux-form';
import PropTypes from 'prop-types';
import { Col, Button, Row } from 'reactstrap';
import AddIcon from 'mdi-react/AddIcon';
import MagnifyIcon from 'mdi-react/MagnifyIcon';
import { isPossiblePhoneNumber } from 'react-phone-number-input';
import { toast } from 'react-toastify';
import ContactValidate from './contactInfoValidate';
import renderField from '../../../shared/components/custom/Field';
import t, { partial } from '../../../util/translation/translation';
import renderCheckBoxField from '../../../shared/components/form/CheckBox';
import renderCountryPhone, {
  extractPhoneValue,
} from '../../log_in/components/CountryPhone';
import FormBox from '../../../shared/components/custom/FormBox';
import InfoBox from '../../../shared/components/custom/InfoBox';
import Api from '../../../util/api';

const s = partial('shared');
const p = partial('AddEmployee');

const ContactInfoForm = ({
  isEdit,
  handleSubmit,
  initialize,
  reset,
  user,
  onUserFound,
  onCancel,
}) => {
  const [searching, setSearching] = useState(false);

  const handleSearchClick = (rawPhone) => {
    const phone = extractPhoneValue(rawPhone);
    if (!phone || !isPossiblePhoneNumber(phone)) {
      toast.error(p('phoneNumberInfo'));
      return;
    }
    setSearching(true);
    Api.users
      .getUserByPhoneOrEmail(phone)
      .then((foundUser) => {
        if (foundUser) {
          onUserFound(foundUser);
        } else {
          toast.info(p('noResultFound'));
        }
      })
      .catch(() => toast.error(p('noResultFound')))
      .finally(() => setSearching(false));
  };

  const handleClose = () => {
    reset();
    onCancel();
  };

  useEffect(() => {
    if (user?.userCloudId) {
      initialize({
        firstName: user.firstname,
        lastName: user.lastname,
        phone: user.phoneNumber,
        email: user.email,
        userId: user.userCloudId,
        companyPosition: user.companyPosition,
        visibleCompany: user.visibleCompany,
        visiblePrivate: user.visiblePrivate,
        isPrimaryContact: user.isPrimaryContact,
        isVIP: user.isVIP,
      });
    } else {
      initialize({
        firstName: null,
        lastName: null,
        phone: null,
        email: null,
        userId: null,
        companyPosition: null,
        visibleCompany: false,
        visiblePrivate: false,
        isPrimaryContact: false,
        isVIP: false,
      });
    }
  }, [user, initialize]);

  return (
    <form className="form" onSubmit={handleSubmit}>
      <Col lg={12} md={12} className="mb-3">
        <FormBox className="form-box--padded">
          <div className="form-card__search-header">
            <MagnifyIcon size={16} />
            <strong>{p('searchForExistingUser')}</strong>
            <span className="form-card__search-optional">
              – {s('optional')}
            </span>
          </div>
          <Row>
            <Col lg={6} md={12}>
              <div className="form__form-group mb-0">
                <Field
                  name="phone"
                  component={renderCountryPhone}
                  placeholder={p('phoneNumberPlaceholder')}
                  onSearch={handleSearchClick}
                  searchLabel={s('search')}
                  searchDisabled={searching}
                />
              </div>
            </Col>
          </Row>
          <small className="form-subtitle">
            {p('searchForExistingUserInstruction')}
          </small>
        </FormBox>
      </Col>
      <Col lg={12} md={12} className="mb-3">
        <Row>
          <Col lg={6} md={12}>
            <div className="form__form-group">
              <span className="form-header">{p('firstName')}</span>
              <Field
                name="firstName"
                component={renderField}
                placeholder={p('firstName')}
              />
            </div>
          </Col>
          <Col lg={6} md={12}>
            <div className="form__form-group">
              <span className="form-header">{p('lastName')}</span>
              <Field
                name="lastName"
                component={renderField}
                placeholder={p('lastName')}
              />
            </div>
          </Col>
        </Row>
        <Row>
          <Col lg={6} md={12}>
            <div className="form__form-group">
              <span className="form-header">{s('email')}*</span>
              <Field
                name="email"
                component={renderField}
                placeholder={p('email')}
              />
            </div>
          </Col>
          <Col lg={6} md={12}>
            <div className="form__form-group">
              <span className="form-header">{p('companyPosition')}</span>
              <Field
                name="companyPosition"
                component={renderField}
                placeholder={p('companyPositionPlaceholder')}
              />
            </div>
          </Col>
        </Row>
        <InfoBox>{p('addContactInfo')}</InfoBox>
        <FormBox className="form-box--padded">
          <Row>
            <Col lg={6} md={12}>
              <div className="checkbox-group">
                <span className="checkbox-group__title">
                  {t('Reports.visibilityInApp')}
                </span>
                <Field
                  name="visibleCompany"
                  component={renderCheckBoxField}
                  label={t('Reports.visibleForPartners')}
                  className="colored-click"
                />
                <Field
                  name="visiblePrivate"
                  component={renderCheckBoxField}
                  label={t('Reports.visibleForPublic')}
                  className="colored-click"
                />
              </div>
            </Col>
            <Col lg={6} md={12}>
              <div className="checkbox-group">
                <span className="checkbox-group__title">Status</span>
                <Field
                  name="isPrimaryContact"
                  component={renderCheckBoxField}
                  label={p('primaryContact')}
                  className="colored-click"
                />
                <Field
                  name="isVIP"
                  component={renderCheckBoxField}
                  label="VIP"
                  className="colored-click"
                />
              </div>
            </Col>
          </Row>
        </FormBox>
      </Col>
      <Col lg={12} md={12}>
        <div className="form-card__actions">
          <Button
            type="submit"
            color={isEdit ? 'warning' : 'success'}
            size="sm"
          >
            {!isEdit && <AddIcon size={16} />}{' '}
            {isEdit ? s('update') : p('addContact')}
          </Button>
          <Button
            type="button"
            color="secondary"
            outline
            size="sm"
            onClick={handleClose}
          >
            {s('close')}
          </Button>
        </div>
      </Col>
    </form>
  );
};

ContactInfoForm.propTypes = {
  isEdit: PropTypes.bool,
  user: PropTypes.shape({
    firstname: PropTypes.string,
    lastname: PropTypes.string,
    userCloudId: PropTypes.string,
    phoneNumber: PropTypes.string,
    email: PropTypes.string,
  }),
  handleSubmit: PropTypes.func.isRequired,
  initialize: PropTypes.func.isRequired,
  reset: PropTypes.func.isRequired,
  onUserFound: PropTypes.func,
  onCancel: PropTypes.func,
};

ContactInfoForm.defaultProps = {
  isEdit: false,
  user: undefined,
  onUserFound: () => {},
  onCancel: () => {},
};

const reduxF = reduxForm({
  form: 'contactInfoForm',
  validate: ContactValidate,
});

export default reduxF(ContactInfoForm);
